import { useAtomValue } from "jotai";
import { useState } from "react";
import { toast } from "sonner";
import { Lightbox } from "@/components/Lightbox";
import {
  type GalleryItem,
  Thumb,
  UploadCard,
} from "@/modules/plus/photos/components/PhotoGalleryParts";
import { atoms } from "@/state";

// Photo gallery for models without a template. No code builder to drive
// matching, so we just show every photo whose code is the model code OR
// starts with `${modelCode}-` (i.e. composite ordering keys that begin with
// this model). The upload field is a plain text input defaulting to the
// model code.
export const BasicPhotoGallery: React.FC<{ modelCode: string }> = ({ modelCode }) => {
  const uploadedQuery = useAtomValue(atoms.photos.uploadedQuery);
  const baseline = useAtomValue(atoms.data.accessoryBaseline);
  const removeMutation = useAtomValue(atoms.photos.remove);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const [lightboxItem, setLightboxItem] = useState<GalleryItem | null>(null);

  const uploaded = uploadedQuery.data ?? {};
  const prefix = `${modelCode}-`;
  const items: GalleryItem[] = [];

  // Baseline image for the model code itself, if any.
  const baselineUrl = baseline[modelCode];
  if (baselineUrl) {
    items.push({
      url: baselineUrl,
      code: modelCode,
      source: "baseline",
      kind: "baseline",
    });
  }

  // Every uploaded photo whose code matches the model code or starts with it.
  for (const [code, photos] of Object.entries(uploaded)) {
    if (code !== modelCode && !code.startsWith(prefix)) {
      continue;
    }
    for (const p of photos) {
      items.push({
        url: p.image_url,
        code,
        source: "uploaded",
        uploadedBy: p.uploaded_by,
        imagePath: p.image_path,
        kind: code === modelCode ? "segment" : "full",
      });
    }
  }

  const onRemove = async (item: GalleryItem) => {
    if (!item.imagePath) {
      return;
    }
    if (!window.confirm(`Delete this photo for ${item.code}? This can't be undone.`)) {
      return;
    }
    try {
      await removeMutation.mutateAsync({ code: item.code, imagePath: item.imagePath });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Remove failed");
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Photo gallery ({items.length})
      </h2>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Upload photos for this model. Use the model code, or a longer ordering key (e.g.{" "}
        <code>{modelCode}-…</code>) to attach a photo to a specific configuration.
      </p>

      <div className="mt-4 flex flex-col gap-5">
        <UploadCard
          defaultCode={modelCode}
          helpText={`Defaults to the model code (${modelCode}) — edit to attach a photo to a specific configuration.`}
        />

        {items.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No photos yet. Upload one above to get started.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {items.map((item, idx) => (
              <Thumb
                // biome-ignore lint/suspicious/noArrayIndexKey: duplicate photos can share code+url; index disambiguates
                key={`${item.kind}-${item.code}-${item.url}-${idx}`}
                item={item}
                signedIn={signedIn}
                busy={removeMutation.isPending}
                onOpen={() => setLightboxItem(item)}
                onRemove={() => onRemove(item)}
              />
            ))}
          </div>
        )}
      </div>

      <Lightbox
        url={lightboxItem?.url ?? null}
        alt={lightboxItem?.code}
        caption={lightboxItem?.code}
        isOpen={lightboxItem !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxItem(null);
          }
        }}
      />
    </section>
  );
};
